/**
 * Pearl shell Shadow DOM host — Figma rebrand CSS.
 *
 * The shell lives inside a shadow root so host page-level styles can
 * never bleed in (nor ours leak out). Mode is `closed` for users and `open`
 * under WebDriver so Playwright E2E can pierce the shell; style encapsulation
 * is identical either way. The host is a zero-size fixed div with
 * `all:initial` to defeat any tag-selector CSS from the host page.
 */

import { COMMON_CSS } from './styles/common.css';
import { ICONS_CSS } from './styles/icons.css';
import { FAB_CSS } from './styles/fab.css';
import { MINI_TAB_CSS } from './styles/mini-tab.css';
import { LOGIN_CSS } from './styles/login.css';
import { PANEL_CSS } from './styles/panel.css';
import { RECORDING_INDICATOR_CSS } from './styles/recording-indicator.css';
import { YIELD_CSS } from './styles/yield.css';
import { TOAST_CSS } from './styles/toast.css';
import { LAUNCHER_HIDDEN_CSS } from './styles/launcher-hidden.css';

export const PEARL_SHELL_HOST_ID = 'pearl-shell-host';
export const PEARL_SHELL_ZOOM_WRAP_ID = 'pw-zoom-wrap';

export const PEARL_SHELL_CSS = [
  COMMON_CSS,
  ICONS_CSS,
  FAB_CSS,
  MINI_TAB_CSS,
  LOGIN_CSS,
  PANEL_CSS,
  RECORDING_INDICATOR_CSS,
  YIELD_CSS,
  TOAST_CSS,
  LAUNCHER_HIDDEN_CSS,
].join('\n');

export interface ShadowHostRefs {
  host: HTMLElement;
  shadow: ShadowRoot;
  zoomWrap: HTMLElement;
  style: HTMLStyleElement;
  /** Disconnects the re-attach observer. Call from destroy() to allow clean removal. */
  disconnectObserver: () => void;
}

/**
 * Creates (or returns the existing) Pearl shell host with a closed shadow root,
 * a zoom-compensation wrapper, and the injected CSS.
 *
 * Idempotent: calling twice returns the same host.
 */
export function createShadowHost(doc: Document): ShadowHostRefs {
  const existing = doc.getElementById(PEARL_SHELL_HOST_ID);
  if (existing) {
    const existingRefs = (
      existing as unknown as { __pearlRefs?: ShadowHostRefs }
    ).__pearlRefs;
    if (existingRefs) return existingRefs;
  }

  const host = doc.createElement('div');
  host.id = PEARL_SHELL_HOST_ID;
  host.setAttribute('data-pearl-shell', 'true');
  // Do not set an inline z-index. Curve's ContextMenu / popup.open scan
  // body > * for node.style.zIndex and place menus at max+1. 2147483647
  // saturates the 32-bit ceiling so Edit/Delete paints behind the notes UI.
  // Visual stacking stays on .pw-root inside the shadow tree.
  host.style.cssText =
    [
      'all: initial',
      'position: fixed',
      'top: 0',
      'left: 0',
      'width: 0',
      'height: 0',
      'overflow: visible',
      'pointer-events: none',
    ].join(' !important; ') + ' !important;';

  doc.body.appendChild(host);

  // Closed by default so PMS page styles/scripts cannot reach into the shell.
  // Under WebDriver (Playwright E2E) we open the root so locators can pierce
  // it — style encapsulation is identical either way; only JS reachability
  // changes. A PMS page cannot spoof navigator.webdriver from the isolated
  // content-script world.
  const shadow = host.attachShadow({
    mode: navigator.webdriver ? 'open' : 'closed',
  });

  const style = doc.createElement('style');
  style.textContent = PEARL_SHELL_CSS;
  shadow.appendChild(style);

  const zoomWrap = doc.createElement('div');
  zoomWrap.id = PEARL_SHELL_ZOOM_WRAP_ID;
  shadow.appendChild(zoomWrap);

  const disconnectObserver = watchForRemoval(doc, host);

  const refs: ShadowHostRefs = {
    host,
    shadow,
    zoomWrap,
    style,
    disconnectObserver,
  };
  (host as unknown as { __pearlRefs: ShadowHostRefs }).__pearlRefs = refs;

  return refs;
}

/**
 * Observes the document for removal of the shell host and re-attaches it.
 * Some PMS hosts replace body content via partial postbacks or AJAX panels,
 * which removes extension-injected elements. This ensures the shell survives
 * those mutations.
 *
 * Returns a disconnect function to stop observing (used by destroy()).
 */
function watchForRemoval(doc: Document, host: HTMLElement): () => void {
  const bodyObserver = new MutationObserver(() => {
    if (!host.isConnected && doc.body) {
      doc.body.appendChild(host);
    }
  });

  bodyObserver.observe(doc.body, { childList: true });

  // Also observe <html> in case the entire <body> is replaced.
  let htmlObserver: MutationObserver | null = null;
  const htmlEl = doc.documentElement;
  if (htmlEl) {
    htmlObserver = new MutationObserver(() => {
      if (!host.isConnected && doc.body) {
        doc.body.appendChild(host);
        bodyObserver.disconnect();
        bodyObserver.observe(doc.body, { childList: true });
      }
    });
    htmlObserver.observe(htmlEl, { childList: true });
  }

  return () => {
    bodyObserver.disconnect();
    if (htmlObserver) htmlObserver.disconnect();
  };
}
